import { Component, ElementRef, Input, ViewChild } from '@angular/core'
import {
  select,
  scaleOrdinal,
  schemeCategory10,
  max,
  scaleLinear,
  scaleBand,
  range,
  format,
  mouse,
} from 'd3'
import { TranslateService } from '@ngx-translate/core'
import { Colors } from '../../common/config/constants'
import { ContextService } from '../../services'

@Component({
  standalone: false,
  selector: 'app-bar-chart-widget',
  templateUrl: './bar-chart-widget.component.html',
  styleUrls: ['./bar-chart-widget.component.scss'],
})
export class BarChartWidgetComponent {
  _data = []
  defualtConfig = {
    mode: 'stacked',
    colors: [Colors.colorAccent],
    unselectedColor: Colors.colorN30,
    getTickInterval: (_) => 12,
  }
  _config: any = { ...this.defualtConfig }
  colorScale = null

  @ViewChild('tooltip', { static: true }) tooltip: ElementRef
  @ViewChild('svg', { static: true }) svg: ElementRef

  get total(): number {
    return this.data && this.data[0] && this.data[0].total
  }

  @Input() title = ''
  @Input() unit = ''
  @Input() isMultiple = false
  @Input() tooltipSuffix = 'visitors'
  @Input() highlight = []
  @Input() stats
  @Input() usePerc = false

  get config() {
    return this._config
  }

  set config(newConfig) {
    newConfig = newConfig || {}
    this._config = { ...this.defualtConfig, ...newConfig }
  }

  @Input()
  get data() {
    return this._data
  }

  set data(value: any[]) {
    if (!value || !value.length) {
      return
    }

    if (this.isMultiple) {
      this._data = value[0].map((d, idx) => {
        const res = {
          label: d.label,
          category: d.category,
          id: d.id,
          value: d.value,
          perc: d.perc,
          compared: this.usePerc ? value[1][idx].perc : value[1][idx].value,
        } as any
        if (d.total) {
          res.total = d.total
          res.comparedTotal = value[1][idx].total
        }
        return res
      })
    } else {
      this._data = value
    }
    this.draw()
  }

  get isVisitsAvailable() {
    return this.context.isVisitsAvailable()
  }

  constructor(
    private context: ContextService,
    private translate: TranslateService,
  ) {}

  getComparisonTotals() {
    return [this.data[0].total, this.data[0].comparedTotal]
  }

  draw() {
    const tooltip = select(this.tooltip.nativeElement)
    const svg = select(this.svg.nativeElement)

    if (this.config.colors) {
      this.colorScale = scaleOrdinal(this.config.colors)
    } else {
      this.colorScale = scaleOrdinal(schemeCategory10)
    }

    const margin = 20
    const containerDims = {
      width: 255,
      height: 125,
    }
    const width = containerDims.width - 2 * margin
    const height = containerDims.height - 2 * margin

    svg.attr('width', containerDims.width).attr('height', containerDims.height)
    svg.selectAll('g').remove()

    const chart = svg
      .append('g')
      .attr('class', 'center')
      .attr('transform', `translate(${margin}, ${margin})`)

    let yMax =
      this.config.mode === 'stacked'
        ? max(this.data, (d) => (this.usePerc ? d.perc : d.value))
        : max(this.data, (d) =>
            max(this.usePerc ? d.perc : d.value, (d2) =>
              this.usePerc ? d2.perc : d2.value,
            ),
          )

    if (this.isMultiple) {
      yMax =
        this.config.mode === 'stacked'
          ? max(this.data, (d) => d.compared)
          : max(this.data, (d) => max(d.compared, (d2) => d2.compared))
    }

    let yMaxScale = 100
    if (!this.usePerc) {
      while (
        yMaxScale <= yMax ||
        yMaxScale % 4 !== 0 ||
        this.isFloat(yMaxScale / 1000 / 4)
      ) {
        yMaxScale += 100
      }
    }

    const yScale = scaleLinear().rangeRound([height, 0]).domain([0, yMaxScale])

    const yScale_2 = scaleLinear().domain([4, 0]).range([0, yMaxScale])

    const xScale0 = scaleBand()
      .range(this.isMultiple ? [0, width - margin] : [margin, width - margin])
      .domain(this.data.map((d) => d.category))
      .padding(0.6)

    const xScale1 = scaleBand()
      .domain(['field1', 'field2'])
      .range([0, xScale0.bandwidth()])
      .padding(0.2)

    const bgGapPercent = 0.25
    const line = chart.append('g').attr('class', 'y-background')

    line
      .selectAll('line')
      .data(range(0, 1 + bgGapPercent, bgGapPercent))
      .enter()
      .append('line')
      .attr('stroke', Colors.colorN15)
      .attr('x1', '5')
      .attr('x2', width)
      .attr('y1', (d) => Math.floor(d * height))
      .attr('y2', (d) => Math.floor(d * height))

    function kFormatter(num: any) {
      return Math.abs(num) > 999
        ? Math.sign(num) * ((Math.abs(num) / 1000) as any).toFixed(0) + 'K'
        : Math.sign(num) * Math.abs(num)
    }

    line
      .selectAll('text')
      .data(range(0, 1 + bgGapPercent, bgGapPercent))
      .enter()
      .append('text')
      .attr('class', 'y-text')
      .attr('text-anchor', 'middle')
      .attr('transform', (_, idx) => `translate(-8,${idx * 20 + idx + 3})`)
      .text((_, idx) => kFormatter(yScale_2(idx)))

    const rx = 2
    const ry = 2

    chart
      .append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${height})`)
      .selectAll('text')
      .data(this.data)
      .enter()
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('x', (d) => xScale0(d.category) + xScale1.bandwidth() + rx + 1)
      .attr('dy', '20px')
      .text((d) => d.category)

    const model_name = chart
      .selectAll('.model_name')
      .data(this.data)
      .enter()
      .append('g')
      .attr('class', 'model_name')
      .attr(
        'transform',
        (d) => `translate(${this.isMultiple ? xScale0(d.category) : 0},0)`,
      )

    model_name
      .selectAll('.bar.field1')
      .data((d) => [d])
      .enter()
      .append('path')
      .attr('class', 'inner-bar bar field1')
      .attr('fill', this.getColor.bind(this))
      .attr('d', (item) =>
        (this.usePerc ? item.perc : item.value)
          ? `
        M${this.isMultiple ? xScale1('field1') : xScale0(item.category)},${
              yScale(this.usePerc ? item.perc : item.value) + ry
            }
        a${rx},${ry} 0 0 1 ${rx},${-ry}
        h${
          (this.isMultiple ? xScale1.bandwidth() : xScale0.bandwidth()) - 2 * rx
        }
        a${rx},${ry} 0 0 1 ${rx},${ry}
        v${height - yScale(this.usePerc ? item.perc : item.value) - ry}
        h${-(this.isMultiple ? xScale1.bandwidth() : xScale0.bandwidth())}Z
      `
          : '',
      )
      .on('mouseenter', (d) => {
        tooltip.select('.name').text(this.translate.instant(d.label) + '  ')
        tooltip
          .select('.value')
          .text(format(',.0f')(this.usePerc ? d.perc : d.value))
        let perc = d.perc
        if (!this.usePerc) {
          const stats = Array.isArray(this.stats) ? this.stats[0] : this.stats
          perc = (d.value / stats) * 100
        }
        tooltip
          .select('.value-percentage')
          .text(format(',.0f')(perc.toFixed(1)) + '%')
        tooltip.classed('hidden', false)
      })
      .on('mousemove', (d, i, nodes) => {
        const mousePos = mouse(nodes[i])
        const x = (this.isMultiple ? xScale0(d.category) : 0) + mousePos[0]
        tooltip.style('left', `${x}px`)
      })
      .on('mouseout', (_) => {
        tooltip.classed('hidden', true)
      })

    if (this.isMultiple) {
      model_name
        .selectAll('.bar.field2')
        .data((d) => [d])
        .enter()
        .append('path')
        .attr('class', 'inner-bar bar field2')
        .attr('fill', (d, i) => this.getColor(d, i, true))
        .attr('d', (item) =>
          item.compared
            ? `
        M${xScale1('field2')},${yScale(item.compared) + ry}
        a${rx},${ry} 0 0 1 ${rx},${-ry}
        h${xScale1.bandwidth() - 2 * rx}
        a${rx},${ry} 0 0 1 ${rx},${ry}
        v${height - yScale(item.compared) - ry}
        h${-xScale1.bandwidth()}Z
      `
            : '',
        )
        .on('mouseenter', (d) => {
          tooltip.select('.name').text(this.translate.instant(d.label) + '  ')
          tooltip.select('.value').text(format(',.0f')(d.compared))
          let perc = d.perc
          if (!this.usePerc) {
            const stats = Array.isArray(this.stats) ? this.stats[1] : this.stats
            perc = (d.compared / stats) * 100
          }
          tooltip
            .select('.value-percentage')
            .text(format(',.0f')(perc.toFixed(1)) + '%')
          tooltip.classed('hidden', false)
        })
        .on('mousemove', (d, i, nodes) => {
          const mousePos = mouse(nodes[i])
          const x = xScale0(d.category) + mousePos[0]
          tooltip.style('left', `${x}px`)
        })
        .on('mouseout', (_) => {
          tooltip.classed('hidden', true)
        })
    }
  }

  private getColor(d, i, secondary = false) {
    let elemColor =
      secondary && typeof secondary === 'boolean'
        ? Colors.colorSecondary
        : this.colorScale(i)
    if (this.highlight.length && this.highlight.indexOf(d.id) === -1) {
      elemColor = this.config.unselectedColor
    }
    return elemColor
  }

  private isFloat(val) {
    return Number(val) === val && val % 1 !== 0
  }
}
